<script setup>
import ChangeDevices from "@/components/Common/ChangeDevices.vue";
import { useDeviceState } from "@/stores/deviceState";
import { ref, onMounted, onUnmounted, watch, computed } from "vue";
import axios from "axios";
import { toastError, sleep } from "@/utils";
import WebRTCRoom from "../../PureWebRTC/webrtcRoom";
import { useVideoCallState } from "@/stores/videoCallState";
import VideoContainer from "@/components/VideoCall/VideoContainer.vue";
import { SectionList } from "@module-system";
import ListContainer from "@module-system/ViewModule/ListContainer.vue";
import SectionToggleContainer from "@module-system/ViewModule/SectionToggleContainer.vue";
import { Actions, Events, EventManager } from "@events";
import VideoCallButton from "@/components/Common/VideoCallButton.vue";
import i18n from "@/i18n";

let validated = ref(false);
let loading = ref(true);
let joined = ref(false);

// #region token validation

const props = defineProps({
  rtcId: {
    type: String,
    required: true,
  },
});

const actionEmitter = EventManager.getActionEmitter(props.rtcId);
const eventEmitter = EventManager.getEventEmitter(props.rtcId);

const urlParams = new URLSearchParams(window.location.search);
const whisperToken = urlParams.get("whisperToken");
const supervisor = urlParams.get("supervisor");
const disableHiddenMode = urlParams.get("disableHiddenMode");

if (!whisperToken) {
  alert("Whisper token is missing");
  window.location.href = "/";
}

async function validateToken() {
  try {
    loading.value = true;
    const response = await axios.post("/api/whisper/validate", {
      token: whisperToken,
      rtcId: props.rtcId,
      initiator: supervisor,
    });
    if (response.data.success) {
      validated.value = true;
    } else {
      toastError(response);
    }
  } catch (err) {
    toastError(err);
  } finally {
    loading.value = false;
  }
}

validateToken();

// #endregion

// #region camera/mic/hidden stuff

const deviceState = useDeviceState();
const localVideo = ref(null);
const stream = ref(null);

const useWhisperCamera = ref(false);
const useWhisperMicrophone = ref(false);
const hiddenMode = ref(true);

watch(localVideo, (value) => {
  if (value) {
    value.srcObject = stream.value;
  }
});

onMounted(async () => {
  if(disableHiddenMode){
    hiddenMode.value = false;
    useWhisperCamera.value = true;
    useWhisperMicrophone.value = true;
  }
  
  await deviceState.ready;
  deviceState.cameraPermission.addEventListener("change", () => {
    deviceChanged();
  });
  deviceState.microphonePermission.addEventListener("change", () => {
    deviceChanged();
  });
  await deviceState.checkPermissions();
  deviceChanged();
});

onUnmounted(() => {
  if (stream.value) {
    stream.value.getTracks().forEach((track) => track.stop());
  }
});

async function deviceChanged() {
  try {
    if (stream.value) {
      stream.value.getTracks().forEach((track) => track.stop());
    }
    const input = await deviceState.getUserMedia();
    watch(
      input,
      (newStream) => {
        if (stream.value) {
          stream.value.getTracks().forEach((track) => track.stop());
        }

        stream.value = newStream;

        if (localVideo.value) {
          localVideo.value.srcObject = newStream;
        }
      },
      { immediate: true },
    );
    console.log("device changed");
  } catch (err) {
    alert(err);
  }
}

// watch hiddenMode if true, hide the video and audio
watch(hiddenMode, (isHidden) => {
  if (isHidden) {
    useWhisperCamera.value = false;
    useWhisperMicrophone.value = false;

    whisperCallState?.setDisableVideoControls(true, "Whisper is in hidden mode");
  } 
  else {
    useWhisperMicrophone.value = true;

    whisperCallState?.setDisableVideoControls(false, "Whisper is out of hidden mode");
  }
});

// watch useWhisperCamera and useWhisperMicrophone if any of them is true, set hiddenMode to false
watch([useWhisperCamera, useWhisperMicrophone], (values) => {
  if (values.some((value) => value)) {
    hiddenMode.value = false;
  }

  // if both of them are false, set hiddenMode to true
  if (!values.some((value) => value)) {
    hiddenMode.value = true;
  }
});
// #endregion

// #region join

const whisperRtcId = ref(null);

/**
 * @type {WebRTCRoom}
 */
let whisperRoom = null;
let whisperCallState = useVideoCallState(null);
let options = {};

async function join() {
  try {
    const resp = await axios.post("/api/whisper/initiate", { token: whisperToken, rtcId: props.rtcId, initiator: supervisor });

    whisperRtcId.value = resp.data.data.whisperRoomId;
    let agentToken = resp.data.data.agentToken;

    whisperCallState = useVideoCallState(whisperRtcId.value);

    whisperCallState.setRole("supervisor");
    whisperCallState.setName(supervisor);

    whisperCallState.setAgentToken(agentToken);

    axios.defaults.headers.common["ECV-AgentToken"] = agentToken;

    // add &agentToken= to the url
    const url = new URL(window.location.href);
    url.searchParams.set("agentToken", agentToken);
    window.history.pushState({}, "", url);

    joined.value = true;

    options.disableAudio = !useWhisperMicrophone.value;
    options.disableVideo = !useWhisperCamera.value;

    whisperRoom = new WebRTCRoom(whisperRtcId.value, options);

    whisperRoom.callEventEmitter.on(Events.WebRTCConnected, () => {
      if (hiddenMode.value) {
        whisperRoom.textChannel.send(`STATUS|AUDIO|${useWhisperMicrophone.value ? "UNMUTED" : "MUTED"}|${whisperRtcId.value}`);
        whisperRoom.textChannel.send(`STATUS|VIDEO|${useWhisperCamera.value ? "UNMUTED" : "MUTED"}|${whisperRtcId.value}`);
      }
      else {
        closeHiddenMode();
      }
    });

    whisperRoom.callEventEmitter.on(Events.SignalingMessageReceived, async (data) => {
      if (data?.type == "whisper:already-joined") {
        whisperCallState?.setDisableVideoControls(true, "Whisper is in hidden mode");

        whisperCallState.snackbar.msg = i18n.global.t("SomeoneElseAlreadyObeserving");
        whisperCallState.snackbar.show = true;
      }
      else if(data?.type == "whisper:remove-user-stream"){
        const remotePeers = whisperCallState.peers.filter((peer) => peer.type === "remote");
        console.log("remotePeers", remotePeers);

        let disconnectedRemotePeer = null;

        // Loop through all remote peers to find the first one with a the specific id track
        for (const remotePeer of remotePeers) {
          const videoTracks = remotePeer?.video?.srcObject?.getTracks()?.filter(track => track.kind == "video");
          
          console.log("remotePeer", remotePeer?.peerName, "videoTracks", videoTracks);

          // Check if any video track is disabled, ended, or not live
          if (videoTracks && videoTracks.some(track => track.id == data.id)) {
            disconnectedRemotePeer = remotePeer;
            break;
          }
        }

        console.log("disconnectedRemotePeer", disconnectedRemotePeer);

        if(disconnectedRemotePeer){
          whisperCallState.removePeer(disconnectedRemotePeer);
        }
      }
      else if(data?.type == "whisper:photos-updated"){
        actionEmitter.emit(Actions.PhotoPanelUpdate);
      }
    });

    eventEmitter.on(Events.PhotoDeleted, (data) => {
      whisperRoom.signaling.sendWrapped({
        type: "whisper:photo-deleted",
      });
    });

    whisperRoom.callEventEmitter.on(Events.PeerVideoElementSet, async () => {
      // Wait 1 second for the peer element to load
      setTimeout(function(){     
        // Then refresh the window size
        window.dispatchEvent(new Event('resize'));
      }, 1000);
    });

    // Disabling call buttons when in hiddenMode
    if(hiddenMode.value) {
      whisperCallState?.setDisableVideoControls(true, "Whisper is in hidden mode");
    }
  } 
  catch (err) {
    toastError(err);
    return;
  }
}
// #endregion

// #region audio/video hidden mode in call actions

async function closeHiddenMode(ignoreMediaControls = true) {
  const wasAllMediaControlsDisabled = !useWhisperCamera.value && !useWhisperMicrophone.value;

  hiddenMode.value = false;

  if(!ignoreMediaControls){
    // Exiting hidden mode makes the supervisor visible+audible: send both camera and mic.
    useWhisperCamera.value = true;
    useWhisperMicrophone.value = true;
  }

  if (wasAllMediaControlsDisabled) {
    // Acquire the FULL camera+mic and renegotiate. disable* is now honored, so requesting audio-only here
    // would leave no camera track for the mute buttons to unmute — acquire both, then apply the mute state.
    options.disableAudio = false;
    options.disableVideo = false;
    await whisperRoom.getUserMedia();
    await whisperRoom.addLocalStream();
  }

  // Apply the desired send state via soft-mute on the now-present tracks (both on here).
  whisperRoom.roomActionController.muteGeneric("Audio", !useWhisperMicrophone.value, false);
  whisperRoom.roomActionController.muteGeneric("Video", !useWhisperCamera.value, false);

  whisperRoom.signaling.sendWrapped({
    type: "whisper:set:hiddenMode:off",
  });

  whisperRoom.textChannel.send(`STATUS|AUDIO|${useWhisperMicrophone.value ? "UNMUTED" : "MUTED"}|${whisperRtcId.value}`);
  whisperRoom.textChannel.send(`STATUS|VIDEO|${useWhisperCamera.value ? "UNMUTED" : "MUTED"}|${whisperRtcId.value}`);
}

const hiddenModeIcon = computed(() => {
  if (hiddenMode.value) {
    return "mdi-account-off";
  }
  return "mdi-account-switch";
});
</script>

<template>
  <v-container v-if="!joined">
    <v-row v-if="loading">
      <v-col sm="12" md="12" lg="3"></v-col>
      <v-col sm="12" md="12" lg="6">
        <v-progress-linear indeterminate color="primary" />
        <v-alert type="info" style="margin-top: 10px">
          {{ $i18n.t("ValidatingToken") }}
        </v-alert>
      </v-col>
      <v-col sm="12" md="12" lg="3"></v-col>
    </v-row>

    <v-row v-if="!loading && validated && !joined">
      <v-col sm="12" md="12" lg="3"></v-col>
      <v-col sm="12" md="12" :lg="useWhisperCamera ? 12 : 6">
        <v-card style="position: relative; z-index: 1">
          <v-card-title> {{ $i18n.t("Whisper") }} </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6" v-if="useWhisperCamera">
                <video muted class="agent-video-call" ref="localVideo" autoplay />
              </v-col>

              <v-col cols="12" :md="useWhisperCamera ? 6 : 12">
                <v-row>
                  <v-col cols="12">
                    <h3>{{ $i18n.t("WhisperSettings") }}</h3>
                  </v-col>
                  <v-col><v-switch color="primary" v-model="hiddenMode" :label="$i18n.t('HiddenMode')" :disabled="disableHiddenMode" /></v-col>
                  <v-col><v-switch color="primary" v-model="useWhisperCamera" :label="$i18n.t('Camera')" :disabled="disableHiddenMode" /></v-col>
                  <v-col><v-switch color="primary" v-model="useWhisperMicrophone" :label="$i18n.t('Microphone')" /></v-col>
                </v-row>

                <change-devices @device-changed="deviceChanged"></change-devices>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-action>
            <v-col>
              <v-btn block @click="join" color="primary">{{ $i18n.t("Join") }}</v-btn>
            </v-col>
          </v-card-action>
        </v-card>
      </v-col>
    </v-row>
  </v-container>

  <div v-if="!loading && validated && joined" class="d-flex justify-content-center align-items-center h-100">
    <div class="video-call">
      <SectionToggleContainer
        class="left-panel"
        :rtc-id="props.rtcId"
        :section-type="SectionList.CallLeftPanel"
        :size="150"></SectionToggleContainer>
      <div class="middle-content">
        <SectionToggleContainer
          class="top-panel"
          :rtc-id="props.rtcId"
          :section-type="SectionList.CallTopPanel"
          :size="100"></SectionToggleContainer>
        <div class="video-call-content">
          <ListContainer :rtc-id="props.rtcId" :section-type="SectionList.CallTopBar"> </ListContainer>
          <div class="call-buttons-list">
            <VideoCallButton :icon="hiddenModeIcon" @click="closeHiddenMode(false)" :disabled="!hiddenMode" />
            <ListContainer :rtc-id="whisperRtcId" :section-type="SectionList.CallButtonsList"></ListContainer>
          </div>
          <VideoContainer :rtc-id="whisperRtcId"></VideoContainer>
        </div>
        <SectionToggleContainer
          class="bottom-panel"
          :rtc-id="props.rtcId"
          :section-type="SectionList.CallBottomPanel"
          :close-disabled="true"
          :size="100"></SectionToggleContainer>
      </div>
      <SectionToggleContainer
        class="right-panel"
        :rtc-id="props.rtcId"
        :section-type="SectionList.CallRightPanel"
        :manuel-open="true"
        :size="150"></SectionToggleContainer>
      <div style="flex-direction: column">
        <ListContainer :rtc-id="whisperRtcId" :section-type="SectionList.CallModal"></ListContainer>
      </div>
      <v-dialog :model-value="whisperCallState.isHeld" persistent width="auto">
        <v-alert :title="$t('INFO')" :text="$t('WEBRTC_CALL_HOLD_MESSAGE')" type="info" icon="mdi-timer-sand"></v-alert>
      </v-dialog>
      <v-snackbar v-model="whisperCallState.snackbar.show" timeout="-1" color="info" bottom center class="snackbar">
        <div class="snackbar-container">
          {{ whisperCallState.snackbar.msg }}
        </div>
      </v-snackbar>
    </div>
  </div>
</template>

<style>
.video-call {
  display: flex;
  height: 100%;
  width: 100%;
}

.left-panel {
  height: 100%;
  border-right: 3px dashed #363636;
}

.middle-content {
  flex: 1;
  flex-grow: 1;
  flex-shrink: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.video-call-content {
  flex: 1;
  width: 100%;
  flex-grow: 1;
  flex-shrink: 1;
  position: relative;
  overflow: hidden;
}

.video-call-content .videos {
  flex-direction: row-reverse;
}

.right-panel {
  height: 100%;
  border-left: 3px dashed #363636;
}

.call-buttons-list {
  position: absolute;
  top: 0;
  left: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9;
  flex-direction: column;
  height: 100%;
}

.call-buttons-list .ecv-call-button {
  margin-top: 10px;
}

.top-panel {
  border-bottom: 3px dashed #363636;
}

.bottom-panel {
  height: 10%;
  border-top: 3px dashed #363636;
}

.snackbar {
  margin-bottom: 50px;

  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10 and IE 11 */
  user-select: none; /* Standard syntax */
}

.snackbar-container {
  width: 100%;
  text-align: center;
  margin: 0 auto;
}

.peer-video {
  background-color: #181818;
}
</style>
